import React, { useEffect, useState } from "react";
import { Chess } from "chess.js";
import { getEngine } from "../lib/browserEngine";
import { isLikelyPhone } from "../lib/device";

const LIVE_DEPTH = isLikelyPhone() ? 14 : 18;
const MATE_CP = 10000;

// White-perspective {cp, mate} from a side-to-move engine line.
function toWhite(fen, info) {
  const sign = fen.split(" ")[1] === "w" ? 1 : -1;
  if (info.mate != null) return { cp: sign * (info.mate > 0 ? MATE_CP : -MATE_CP), mate: sign * info.mate };
  return { cp: sign * info.cp, mate: null };
}

function label({ cp, mate }) {
  if (mate != null) return `M${Math.abs(mate)}`;
  if (cp == null) return "–";
  if (Math.abs(cp) >= MATE_CP - 100) return "#";
  const v = Math.abs(cp / 100).toFixed(1);
  return cp >= 0 ? `+${v}` : `-${v}`;
}

// 0..100 share of the bar for White (logistic, like most eval bars).
const whiteShare = (cp) => (cp == null ? 50 : 100 / (1 + Math.exp(-cp / 250)));

function useLiveEval(fen, savedCp, live) {
  const [ev, setEv] = useState({ cp: savedCp, mate: null, depth: null });
  useEffect(() => {
    setEv({ cp: savedCp, mate: null, depth: null });
    if (!live || !fen || fen === "start" || new Chess(fen).isGameOver()) return undefined;
    const engine = getEngine();
    const t = setTimeout(() => {
      engine.startLive(fen, LIVE_DEPTH, (info) => setEv({ ...toWhite(fen, info), depth: info.depth }));
    }, 150);
    return () => { clearTimeout(t); engine.stopLive(); };
  }, [fen, savedCp, live]);
  return ev;
}

export const EvalBar = ({ fen, savedCp, orientation = "white", live = true }) => {
  const ev = useLiveEval(fen, savedCp, live);
  const share = ev.mate != null ? (ev.mate > 0 ? 100 : 0) : whiteShare(ev.cp);
  const whiteOnBottom = orientation === "white";
  return (
    <div className="flex flex-col items-center gap-1 select-none" data-testid="eval-bar">
      <span className="font-mono text-[10px] sm:text-xs font-semibold text-[#EFE8D8] h-4 leading-4 whitespace-nowrap" data-testid="eval-bar-score">
        {label(ev)}
      </span>
      <div
        className={`relative w-3 sm:w-5 flex-1 min-h-[200px] rounded-sm overflow-hidden border border-[rgba(239,232,216,0.15)] bg-[#2A2622] flex ${whiteOnBottom ? "flex-col-reverse" : "flex-col"}`}
        title="Engine evaluation (White's view)"
      >
        <div className="bg-[#EFE8D8] transition-[height] duration-500 ease-out" style={{ height: `${share}%` }} data-testid="eval-bar-fill" />
      </div>
      <span className="font-mono text-[10px] text-[#A8A196] h-4 leading-4" data-testid="eval-bar-depth">
        {ev.depth ? `d${ev.depth}` : live ? "…" : ""}
      </span>
    </div>
  );
};
